import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Suspense, lazy } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ErrorBoundary } from "./components/ErrorBoundary";
import Layout from "./components/Layout";
import LoginPage from "./pages/LoginPage";
import SsoCallback from "./pages/SsoCallback";
import MarketingSite from "./pages/MarketingSite";
import "./App.css";

// Lazy-loaded page components (code splitting)
const Dashboard = lazy(() => import("./pages/CustomDashboard"));
const BusinessSetup = lazy(() => import("./pages/BusinessSetup"));
const RiskRegister = lazy(() => import("./pages/RiskRegister"));
const RiskDetail = lazy(() => import("./pages/RiskDetail"));
const RiskMatrix = lazy(() => import("./pages/RiskMatrix"));
const DocumentReview = lazy(() => import("./pages/DocumentReview"));
const InterviewAssistant = lazy(() => import("./pages/InterviewAssistant"));
const ScenarioPlanning = lazy(() => import("./pages/ScenarioPlanning"));
const DecisionCoach = lazy(() => import("./pages/DecisionCoach"));
const StrategicRisk = lazy(() => import("./pages/StrategicRisk"));
const RiskFinancing = lazy(() => import("./pages/RiskFinancing"));
const CaptiveManagement = lazy(() => import("./pages/CaptiveManagement"));
const BoardReports = lazy(() => import("./pages/BoardReports"));
const ActionTracking = lazy(() => import("./pages/ActionTracking"));
const KRIDashboard = lazy(() => import("./pages/KRIDashboard"));
const ControlLibrary = lazy(() => import("./pages/ControlLibrary"));
const Escalations = lazy(() => import("./pages/Escalations"));
const Alerts = lazy(() => import("./pages/Alerts"));
const Approvals = lazy(() => import("./pages/Approvals"));
const RiskCorrelation = lazy(() => import("./pages/RiskCorrelation"));
const AggregateExposure = lazy(() => import("./pages/AggregateExposure"));
const MonteCarloSimulation = lazy(() => import("./pages/MonteCarloSimulation"));
const ExternalIntelligence = lazy(() => import("./pages/ExternalIntelligence"));
const EmergingRiskRadar = lazy(() => import("./pages/EmergingRiskRadar"));
const TransferOptimization = lazy(() => import("./pages/TransferOptimization"));
const PropertyManagement = lazy(() => import("./pages/PropertyManagement"));
const COITracking = lazy(() => import("./pages/COITracking"));
const ClaimsMonitor = lazy(() => import("./pages/ClaimsMonitor"));
const CultureAssessment = lazy(() => import("./pages/CultureAssessment"));
const PublicSurvey = lazy(() => import("./pages/PublicSurvey"));
const RiskSurveys = lazy(() => import("./pages/RiskSurveys"));
const PublicRiskSurvey = lazy(() => import("./pages/PublicRiskSurvey"));
const LossEvents = lazy(() => import("./pages/LossEvents"));
const TPRM = lazy(() => import("./pages/TPRM"));
const RiskAppetite = lazy(() => import("./pages/RiskAppetite"));
const Settings = lazy(() => import("./pages/Settings"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const SecurityCompliance = lazy(() => import("./pages/SecurityCompliance"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const CookiePolicy = lazy(() => import("./pages/CookiePolicy"));
const DPA = lazy(() => import("./pages/DPA"));
const LegalIndex = lazy(() => import("./pages/LegalIndex"));
const HistoryDashboard = lazy(() => import("./pages/HistoryDashboard"));
const ScoreEngine = lazy(() => import("./pages/ScoreEngine"));
const TenantManagement = lazy(() => import("./pages/TenantManagement"));
const PlatformAdmin = lazy(() => import("./pages/PlatformAdmin"));
const TenantAdmin = lazy(() => import("./pages/TenantAdmin"));
const DecisionDossiers = lazy(() => import("./pages/DecisionDossiers"));
const DossierDetail = lazy(() => import("./pages/DossierDetail"));
const GroupView = lazy(() => import("./pages/GroupView"));
const ScenariosPreparedness = lazy(() => import("./pages/ScenariosPreparedness"));
const TabletopGuestLanding = lazy(() => import("./pages/TabletopGuestLanding"));
const LiveSessionCockpit = lazy(() => import("./pages/LiveSessionCockpit"));
const SetupGuide = lazy(() => import("./pages/SetupGuide"));

function PageLoader() {
  return (
    <div className="min-h-[60vh] flex items-center justify-center">
      <div className="animate-pulse text-slate-400">Loading...</div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0a0e1a]">
        <div className="animate-pulse text-slate-400">Loading...</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <BrowserRouter>
      <ErrorBoundary>
      <AuthProvider>
        <div className="min-h-screen bg-background">
          <div className="noise-overlay" />
          <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* Marketing Site at root */}
            <Route path="/" element={<MarketingSite />} />
            <Route path="/security" element={<SecurityCompliance />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />

            {/* Login / Register */}
            <Route path="/login" element={<LoginPage />} />
            <Route path="/sso/callback" element={<SsoCallback />} />

            {/* Main Application under /app - Protected */}
            <Route
              path="/app"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="setup" element={<BusinessSetup />} />
              <Route path="risks" element={<RiskRegister />} />
              <Route path="risks/:riskId" element={<RiskDetail />} />
              <Route path="matrix" element={<RiskMatrix />} />
              <Route path="documents" element={<DocumentReview />} />
              <Route path="interviews" element={<InterviewAssistant />} />
              <Route path="scenarios" element={<ScenariosPreparedness />} />
              <Route path="tabletop/live/:exerciseId" element={<LiveSessionCockpit />} />
              <Route path="coach" element={<DecisionCoach />} />
              <Route path="decisions" element={<DecisionDossiers />} />
              <Route path="decisions/:dossierId" element={<DossierDetail />} />
              <Route path="strategic" element={<StrategicRisk />} />
              <Route path="financing" element={<RiskFinancing />} />
              <Route path="captive" element={<CaptiveManagement />} />
              <Route path="reports" element={<BoardReports />} />
              <Route path="actions" element={<ActionTracking />} />
              <Route path="kris" element={<KRIDashboard />} />
              <Route path="controls" element={<ControlLibrary />} />
              <Route path="escalations" element={<Escalations />} />
              <Route path="alerts" element={<Alerts />} />
              <Route path="approvals" element={<Approvals />} />
              <Route path="correlation" element={<RiskCorrelation />} />
              <Route path="exposure" element={<AggregateExposure />} />
              <Route path="montecarlo" element={<MonteCarloSimulation />} />
              <Route path="intelligence" element={<ExternalIntelligence />} />
              <Route path="emerging-risks" element={<EmergingRiskRadar />} />
              <Route path="transfer" element={<TransferOptimization />} />
              <Route path="property" element={<PropertyManagement />} />
              <Route path="coi" element={<COITracking />} />
              <Route path="claims" element={<ClaimsMonitor />} />
              <Route path="loss-events" element={<LossEvents />} />
              <Route path="culture" element={<CultureAssessment />} />
              <Route path="risk-surveys" element={<RiskSurveys />} />
              <Route path="tprm" element={<TPRM />} />
              <Route path="risk-appetite" element={<RiskAppetite />} />
              <Route path="history" element={<HistoryDashboard />} />
              <Route path="score-engine" element={<ScoreEngine />} />
              <Route path="tenants" element={<TenantManagement />} />
              <Route path="setup-guide" element={<SetupGuide />} />
              <Route path="group" element={<GroupView />} />
              <Route path="admin" element={<PlatformAdmin />} />
              <Route path="org-admin" element={<TenantAdmin />} />
              <Route path="settings" element={<Settings />} />
            </Route>

            {/* Public survey routes - outside Layout */}
            <Route path="survey/:accessCode" element={<PublicSurvey />} />
            <Route path="risk-survey/:surveyId" element={<PublicRiskSurvey />} />

            {/* Tabletop guest magic-link landing (public) */}
            <Route path="tabletop/join/:token" element={<TabletopGuestLanding />} />

            {/* Legal Pages */}
            <Route path="terms" element={<TermsOfService />} />
            <Route path="legal" element={<LegalIndex />} />
            <Route path="legal/cookies" element={<CookiePolicy />} />
            <Route path="legal/dpa" element={<DPA />} />
          </Routes>
          </Suspense>
          <Toaster position="top-right" richColors />
        </div>
      </AuthProvider>
      </ErrorBoundary>
    </BrowserRouter>
  );
}

export default App;
